<template>
    <div class="relative grid w-full grid-cols-[auto_1fr_auto] gap-2">
        <!-- All player rows -->
        <PlayerScoreBar
            v-for="player in orderedPlayers"
            :key="player.id"
            :player="player"
            :color="getProfileColor(player.id)"
            :filled-bars="getFilledBars(player)"
            :mode="getBarMode(player)"
            :can-interact="canPlayerInteract(player)"
            :can-remove="canPlayerRemove(player)"
            :transparent="shouldBeTransparent(player)"
            :show-dead-state="isOtherPlayer(player) && getPlayerLives(player.id) === 0"
            :killed="isEffectivelyKilled(player)"
            :is-active="player.id === currentPlayer?.id"
            :target-section="getPlayerTarget(player.id)"
            :is-killer="getPlayerIsKiller(player.id)"
            @click-plus="handleClickPlus(player)"
            @click-minus="handleClickMinus(player)"
            @click-kill="handleClickKill(player)"
        />

        <!-- Complete Turn / Undo buttons -->
        <div class="relative col-span-3 bg-zinc-700 h-12 clip-diagonal-corners">
            <div class="inset-0.5 clip-diagonal-corners absolute flex gap-2 bg-black">
                <img
                    src="@/assets/images/games/killer/undo.svg"
                    class="h-full"
                    :class="
                        totalClicks > 0
                            ? 'cursor-pointer hover:bg-gray-100'
                            : 'cursor-not-allowed opacity-50'
                    "
                    :title="$t('killer.reset_pending_clicks')"
                    @click="resetAllClicks"
                />
                <div
                    class="flex-1 relative m-1 clip-diagonal-corners"
                    :style="{ backgroundColor: currentPlayerColor }"
                    :class="isCompletingTurn ? 'cursor-not-allowed opacity-50' : ''"
                    data-testid="killer-confirm-turn"
                    @click="confirmDartSelections"
                >
                <div class="mt-1 w-full absolute bg-black/10 text-md font-title clip-diagonal-corners flex cursor-pointer items-center justify-center p-1 text-center font-bold text-white uppercase transition-all select-none">

                    <div v-if="isCompletingTurn" class="flex items-center gap-2">
                        <div
                        class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                        ></div>
                        <span>{{ $t('killer.completing') }}</span>
                    </div>
                    <span v-else>{{ $t('killer.complete_turn') }}</span>
                </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PlayerScoreBar from './PlayerScoreBar.vue'
import { useMatchStore } from '@/stores/match.js'
import { DART_TYPES } from '@/constants/dartTypes.js'

export default {
    name: 'KillerPlayerList',
    components: { PlayerScoreBar },
    props: {
        currentPlayer: {
            type: Object,
            required: true,
        },
        profiles: {
            type: Array,
            default: () => [],
        },
        allProfileStates: {
            type: Array,
            default: () => [],
        },
        getProfileColor: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isCompletingTurn: false,
            isSubmittingKillerDart: false,
            /** Pending click counts per player id. Positive = hits on own target / kills on other. */
            pendingClicks: {},
            /** Number of darts already submitted mid-turn (for killer transition). */
            dartsAlreadySubmitted: 0,
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        currentPlayerState() {
            if (!this.currentPlayer) return null
            return this.allProfileStates.find((state) => state.team === this.currentPlayer.team)
        },
        isCurrentPlayerKiller() {
            return this.currentPlayerState?.is_killer || false
        },
        /** Effective hits = server hits + pending own clicks */
        effectiveHits() {
            return this.currentPlayerHits + (this.pendingClicks[this.currentPlayer?.id] || 0)
        },
        /** Whether player has become killer via pending clicks (hits >= 3) */
        becameKillerThisTurn() {
            return !this.isCurrentPlayerKiller && this.effectiveHits >= 3
        },
        /** Treat player as killer if server says so OR pending clicks made them one */
        effectiveIsKiller() {
            return this.isCurrentPlayerKiller || this.becameKillerThisTurn
        },
        currentPlayerHits() {
            return this.currentPlayerState?.hits_on_own_target || 0
        },
        currentPlayerLives() {
            return this.currentPlayerState?.lives || 0
        },
        currentPlayerTarget() {
            return this.currentPlayerState?.number || null
        },
        orderedPlayers() {
            const current = this.profiles.find((p) => p.id === this.currentPlayer?.id)
            const others = this.profiles.filter((p) => p.id !== this.currentPlayer?.id)
            return current ? [current, ...others] : others
        },
        /** Total absolute clicks across all players this turn */
        totalClicks() {
            return Object.values(this.pendingClicks).reduce((sum, v) => sum + Math.abs(v), 0)
        },
        /** Max click capacity accounts for darts already submitted mid-turn */
        maxClicks() {
            return (3 - this.dartsAlreadySubmitted) * 3
        },
        canStillClick() {
            return this.totalClicks < this.maxClicks
        },
        currentPlayerColor() {
            return this.getProfileColor(this.currentPlayer?.id)
        },
    },
    emits: ['confirm-selection', 'reset-selection'],
    methods: {
        isOtherPlayer(player) {
            return player.id !== this.currentPlayer?.id
        },
        getEffectiveLives(playerId) {
            const lives = this.getPlayerLives(playerId)
            if (playerId === this.currentPlayer?.id) return lives
            const kills = this.pendingClicks[playerId] || 0
            return Math.max(0, lives - kills)
        },
        isEffectivelyKilled(player) {
            if (player.id === this.currentPlayer?.id) return false
            return this.getEffectiveLives(player.id) === 0 && this.getPlayerLives(player.id) > 0
        },
        getPlayerLives(playerId) {
            const player = this.profiles.find((p) => p.id === playerId)
            if (!player) return 0
            const teamState = this.allProfileStates.find((state) => state.team === player.team)
            return teamState?.lives || 0
        },
        getPlayerTarget(playerId) {
            const player = this.profiles.find((p) => p.id === playerId)
            if (!player) return null
            const teamState = this.allProfileStates.find((state) => state.team === player.team)
            return teamState?.number || null
        },
        getPlayerIsKiller(playerId) {
            if (playerId === this.currentPlayer?.id) {
                return this.effectiveIsKiller
            }
            const player = this.profiles.find((p) => p.id === playerId)
            if (!player) return false
            const teamState = this.allProfileStates.find((state) => state.team === player.team)
            return teamState?.is_killer || false
        },
        getFilledBars(player) {
            if (player.id === this.currentPlayer?.id) {
                // When killer (server-side), show lives
                if (this.isCurrentPlayerKiller) return this.currentPlayerLives
                // Not yet killer: show server hits + pending clicks
                return this.effectiveHits
            }
            // Other players: server lives minus pending kill clicks
            const lives = this.getPlayerLives(player.id)
            const kills = this.pendingClicks[player.id] || 0
            return Math.max(0, lives - kills)
        },
        getBarMode(player) {
            if (player.id === this.currentPlayer?.id) {
                // Current player always gets plus/minus
                return 'score'
            }
            // Other players get kill button
            return 'kill'
        },
        canPlayerInteract(player) {
            if (this.isSubmittingKillerDart) return false
            if (player.id === this.currentPlayer?.id) {
                // When effectively killer, can't add more own-target clicks
                if (this.effectiveIsKiller) return false
                return this.currentPlayerTarget && this.canStillClick
            }
            // Other players can be killed only when effectively killer and not already effectively dead
            return (
                this.effectiveIsKiller &&
                this.getEffectiveLives(player.id) > 0 &&
                this.canStillClick
            )
        },
        canPlayerRemove(player) {
            if (player.id === this.currentPlayer?.id) {
                // Can undo own plus clicks (but not if already submitted for killer transition)
                if (this.isCurrentPlayerKiller) return false
                return (this.pendingClicks[player.id] || 0) > 0
            }
            // Can undo kill clicks on other players
            return (this.pendingClicks[player.id] || 0) > 0
        },
        shouldBeTransparent(player) {
            if (player.id === this.currentPlayer?.id) {
                // Half transparent when effectively killer
                return this.effectiveIsKiller
            }
            // Other players are transparent when current player is NOT effectively killer
            return !this.effectiveIsKiller
        },
        async handleClickPlus(player) {
            if (!this.canStillClick) return
            if (player.id !== this.currentPlayer?.id) return
            if (this.effectiveIsKiller) return

            const newClicks = (this.pendingClicks[player.id] || 0) + 1
            this.pendingClicks = {
                ...this.pendingClicks,
                [player.id]: newClicks,
            }

            // If hits + pending clicks reach 3, auto-submit a dart to become killer
            const newEffectiveHits = this.currentPlayerHits + newClicks
            if (newEffectiveHits >= 3) {
                await this.submitKillerTransitionDart(newClicks)
            }
        },
        handleClickMinus(player) {
            const current = this.pendingClicks[player.id] || 0
            if (current <= 0) return

            this.pendingClicks = {
                ...this.pendingClicks,
                [player.id]: current - 1,
            }
        },
        handleClickKill(player) {
            if (!this.canStillClick) return
            if (player.id === this.currentPlayer?.id) return
            if (!this.effectiveIsKiller) return
            if (this.getEffectiveLives(player.id) <= 0) return

            this.pendingClicks = {
                ...this.pendingClicks,
                [player.id]: (this.pendingClicks[player.id] || 0) + 1,
            }
        },
        /**
         * Submit a single dart for the own-target clicks to trigger killer status on server.
         * Converts the accumulated own-target clicks into optimal S/D/T and submits as dart 1.
         */
        async submitKillerTransitionDart(clicks) {
            if (this.isSubmittingKillerDart) return
            this.isSubmittingKillerDart = true

            try {
                const section = this.currentPlayerTarget
                if (!section) return

                const dart = this.clicksToDart(clicks, section)
                await this.matchStore.submitDart({
                    profile_id: this.currentPlayer.id,
                    dart_number: dart.dart_number,
                    dart_type: dart.dart_type,
                    dart_index: 1,
                })

                // Mark these clicks as submitted: clear own pending, track dart used
                this.dartsAlreadySubmitted = 1
                this.pendingClicks = {
                    ...this.pendingClicks,
                    [this.currentPlayer.id]: 0,
                }
            } catch (error) {
                console.error('Failed to submit killer transition dart:', error)
            } finally {
                this.isSubmittingKillerDart = false
            }
        },
        /** Convert a click count into a single optimal dart */
        clicksToDart(clicks, section) {
            if (clicks >= 3) return { dart_type: DART_TYPES.TRIPLE, dart_number: section }
            if (clicks === 2) return { dart_type: DART_TYPES.DOUBLE, dart_number: section }
            return { dart_type: DART_TYPES.SINGLE, dart_number: section }
        },
        resetAllClicks() {
            if (this.totalClicks === 0 && this.dartsAlreadySubmitted === 0) return
            // Note: if a killer transition dart was already submitted, we can't undo it.
            // Only reset pending clicks for remaining darts.
            this.pendingClicks = {}
        },
        /**
         * Build darts from pending clicks, same pattern as CamelraceGame.
         * Each player's click count is turned into optimal dart combos
         * (Triple > Double > Single) targeting that player's section number.
         */
        buildDartsFromClicks() {
            const darts = []

            for (const player of this.orderedPlayers) {
                const clicks = Math.abs(this.pendingClicks[player.id] || 0)
                if (clicks === 0) continue

                const section = this.getPlayerTarget(player.id)
                if (!section) continue

                let remaining = clicks
                while (remaining > 0) {
                    if (remaining >= 3) {
                        darts.push({ dart_type: DART_TYPES.TRIPLE, dart_number: section })
                        remaining -= 3
                    } else if (remaining === 2) {
                        darts.push({ dart_type: DART_TYPES.DOUBLE, dart_number: section })
                        remaining -= 2
                    } else {
                        darts.push({ dart_type: DART_TYPES.SINGLE, dart_number: section })
                        remaining -= 1
                    }
                }
            }

            // Pad to exactly the remaining dart slots with misses
            const remainingSlots = 3 - this.dartsAlreadySubmitted
            while (darts.length < remainingSlots) {
                darts.push({ dart_type: DART_TYPES.MISS, dart_number: 0 })
            }

            return darts.slice(0, remainingSlots)
        },
        async confirmDartSelections() {
            if (!this.currentPlayer || this.isCompletingTurn) return

            this.isCompletingTurn = true
            try {
                const profileId = this.currentPlayer.id
                const darts = this.buildDartsFromClicks()

                // Clear pending clicks BEFORE submitting so server responses
                // don't cause double-subtraction in getFilledBars
                this.pendingClicks = {}

                for (let i = 0; i < darts.length; i++) {
                    await this.matchStore.submitDart({
                        profile_id: profileId,
                        dart_number: darts[i].dart_number,
                        dart_type: darts[i].dart_type,
                        dart_index: this.dartsAlreadySubmitted + i + 1,
                    })
                }

                this.dartsAlreadySubmitted = 0
                this.$emit('confirm-selection')
            } catch (error) {
                console.error('Failed to complete turn:', error)
            } finally {
                this.isCompletingTurn = false
            }
        },
    },
}
</script>
